import {
  WorkItemBulkOperationType,
  WorkItemLogEvent,
} from '@robocorp/robocloud-orchestrator-interface';
import { ListWorkItemsInput } from '@robocorp/robocloud-search-iternal-sdk';

import { WorkItemDTO, WorkItemPayload } from '~/types/resources';
import { integrationProcessApi, taskDataApi, dashboardApi } from '~/utils/api';
import { externalApi } from '~/utils/api/external';
import { WithWorkspaceId } from '~/types/common';
import { WorkItemQueryArgs } from '../searchAPI';

export type DownloadLinkBody = {
  url: string;
};

export type UploadLinkBody = {
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: any;
};

type WorkItemBulkFilterTypes = Pick<
  WorkItemQueryArgs,
  | 'search'
  | 'activityId'
  | 'activityRunId'
  | 'exceptionType'
  | 'processRunId'
  | 'state'
  | 'sortOrder'
  | 'sortBy'
  | 'createTs.gte'
  | 'createTs.lte'
>;

export const retryWorkItem = async ({
  workspaceId,
  processId,
  workItemId,
}: { processId: string; workItemId: string } & WithWorkspaceId) =>
  integrationProcessApi.post<WorkItemDTO>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items/${workItemId}/retry`,
  );

export const deleteWorkItem = async ({
  workspaceId,
  processId,
  workItemId,
}: { processId: string; workItemId: string } & WithWorkspaceId) =>
  integrationProcessApi.delete<WorkItemDTO>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items/${workItemId}`,
  );

export const discardWorkItem = async ({
  workspaceId,
  processId,
  workItemId,
}: { processId: string; workItemId: string } & WithWorkspaceId) =>
  integrationProcessApi.post<WorkItemDTO>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items/${workItemId}/complete`,
  );
export const getProcessWorkItemById = async ({
  workspaceId,
  pid,
  id,
  includeData = true,
}: { pid: string; id: string; includeData?: boolean } & WithWorkspaceId) =>
  integrationProcessApi.get<WorkItemDTO>(
    `/workspaces/${workspaceId}/processes/${pid}/work-items/${id}`,
    {
      includeData,
    },
  );

export const createWorkItem = async ({
  workspaceId,
  pid,
  payload,
}: { pid: string; payload: string } & WithWorkspaceId) =>
  integrationProcessApi.post<WorkItemDTO>(
    `/workspaces/${workspaceId}/processes/${pid}/work-items`,
    {
      payload,
    },
  );

export const updateWorkItemPayload = async ({
  workspaceId,
  workItemId,
  payload,
}: { workItemId: string; payload: string } & WithWorkspaceId) =>
  taskDataApi.put<WorkItemPayload>(
    `/workspaces/${workspaceId}/workitems/${workItemId}/data`,
    payload,
  );

export const deleteWorkItemPayload = async ({
  workspaceId,
  workItemId,
}: { workItemId: string } & WithWorkspaceId) =>
  taskDataApi.delete(`/workspaces/${workspaceId}/workitems/${workItemId}/data`);

export const getWorkItemFileDownloadLink = async ({
  workspaceId,
  workItemId,
  fileId,
}: { workItemId: string; fileId: string } & WithWorkspaceId) =>
  taskDataApi.get<DownloadLinkBody>(
    `/workspaces/${workspaceId}/workitems/${workItemId}/files/${fileId}`,
  );

export const getWorkItemFileUploadLink = async ({
  workspaceId,
  workItemId,
  fileName,
  fileSize,
}: {
  workItemId: string;
  fileName: string;
  fileSize: number;
} & WithWorkspaceId) =>
  taskDataApi.post<UploadLinkBody>(`/workspaces/${workspaceId}/workitems/${workItemId}/files`, {
    fileName,
    fileSize,
  });

// eslint-disable-next-line
export const uploadWorkItemFile = async (uploadUrl: string, form: object) =>
  externalApi.post<any>(uploadUrl, form, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const deleteWorkItemFile = async ({
  workspaceId,
  workItemId,
  fileId,
}: { workItemId: string; fileId: string } & WithWorkspaceId) =>
  taskDataApi.delete(`/workspaces/${workspaceId}/workitems/${workItemId}/files/${fileId}`);

export const triggerWorkItemBulkOperation = async ({
  workspaceId,
  processId,
  args,
}: {
  processId: string;
  args:
    | { operation: WorkItemBulkOperationType; workItemIds: string[] }
    | {
        operation: WorkItemBulkOperationType;
        filters: WorkItemBulkFilterTypes;
      };
} & WithWorkspaceId) => {
  const params =
    'workItemIds' in args
      ? { workItemIds: args.workItemIds }
      : {
          searchFilter: {
            processRunId: args.filters.processRunId,
            activityId: args.filters.activityId?.join(','),
            activityRunId: args.filters.activityRunId,
            state: args.filters.state?.join(','),
            exceptionType: args.filters.exceptionType,
            search: args.filters.search,
            sortBy: args.filters.sortBy,
            sortOrder: args.filters.sortOrder,
            'createTs.lte': args.filters['createTs.lte'],
            'createTs.gte': args.filters['createTs.gte'],
          } satisfies Omit<ListWorkItemsInput, 'workspaceId' | 'processId'>,
        };

  return integrationProcessApi.post<{ accepted: boolean }>(
    `/workspaces/${workspaceId}/processes/${processId}/work-items/bulk-operation`,
    { operation: args.operation, ...params },
  );
};

export const getWorkItemEventLog = async ({
  workspaceId,
  workItemId,
}: {
  workspaceId: string;
  workItemId: string;
}) =>
  dashboardApi.get<{ data: WorkItemLogEvent[] }>(
    `/workspaces/${workspaceId}/work-items/${workItemId}/log-events`,
  );
